import { useEffect, useId, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Form, Link, useSearchParams, useSubmit } from 'react-router'
import MarketingImage from '#app/components/marketing/image.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { PublicInput } from '#app/components/ui/input.tsx'
import { Spinner } from '#app/components/ui/spinner.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { usePublicContext } from '#app/routes/($lang)+/__public-context.tsx'
import { BASE_URL } from '#app/utils/config.ts'
import { prisma } from '#app/utils/db.server.ts'
import { i18n } from '#app/utils/i18n.tsx'
import { generateMetaTranslationLoader } from '#app/utils/i18next.server.ts'
import { generateTranslatedMeta } from '#app/utils/meta.ts'
import { cn, getMaterialDesignImgSrc, useDebounce } from '#app/utils/misc.tsx'
import routes from '#routes/index.tsx'
import { type Route } from './+types/search'

export async function loader({ request, params }: Route.LoaderArgs) {
	let { lang } = params
	if (!lang) lang = i18n.fallbackLng
	const url = new URL(request.url)
	const meta = await generateMetaTranslationLoader({
		request,
		keys: {
			title: 'search.title',
			description: 'search.description',
			canonical: `${BASE_URL}${routes.public.search.route({})}`,
		},
	})

	if (
		!url.searchParams.has('search') ||
		url.searchParams.get('search') === ''
	) {
		return {
			search: null,
			meta,
		}
	}

	const searchTerm = url.searchParams.get('search')!

	const articles = await prisma.post.findMany({
		where: {
			published: true,
			OR: [
				{
					translations: {
						some: {
							languageCode: lang,
							title: {
								contains: searchTerm,
							},
						},
					},
				},
				{
					translations: {
						some: {
							languageCode: lang,
							slug: {
								contains: searchTerm,
							},
						},
					},
				},
				{
					translations: {
						some: {
							languageCode: lang,
							content: {
								contains: searchTerm,
							},
						},
					},
				},
			],
		},
		select: {
			categories: {
				take: 1,
				select: {
					translations: {
						where: {
							languageCode: lang,
						},
						select: {
							name: true,
						},
					},
				},
			},
			translations: {
				where: {
					languageCode: lang,
				},
				take: 1,
				select: {
					slug: true,
					title: true,
					content: true,
				},
			},
		},
		orderBy: {
			publishedAt: 'desc',
		},
	})

	for (const article of articles) {
		const index = article.translations[0]?.content
			.toLowerCase()
			.indexOf(searchTerm.toLowerCase())

		// If found, slice the content to show only a snippet around the search term
		if (index !== undefined && index >= 0) {
			const start = Math.max(0, index - 30)
			const end = Math.min(
				article.translations[0]!.content.length,
				index + searchTerm.length + 30,
			)
			article.translations[0]!.content =
				(start > 0 ? '...' : '') +
				article.translations[0]!.content.slice(start, end) +
				(end < article.translations[0]!.content.length ? '...' : '')
		}

		// If not found, search in the title and set content to empty
		else {
			article.translations[0]!.content = ''
		}
	}

	const collections = await prisma.collection.findMany({
		where: {
			OR: [
				{
					name: {
						contains: searchTerm,
					},
				},
				{
					translations: {
						some: {
							description: {
								contains: searchTerm,
							},
						},
					},
				},
				{
					variants: {
						some: {
							OR: [
								{
									sapCode: {
										contains: searchTerm,
									},
								},
								{
									finish: {
										translations: {
											some: {
												name: {
													contains: searchTerm,
												},
											},
										},
									},
								},
							],
						},
					},
				},
			],
			published: true,
		},
		select: {
			slug: true,
			id: true,
			designsImages: {
				select: {
					id: true,
					altText: true,
				},
				take: 1,
			},
			name: true,
		},
	})

	return {
		meta,
		search: searchTerm,
		collections,
		articles,
		lang,
	}
}

export const meta = generateTranslatedMeta

export default function SearchPage({ loaderData }: Route.ComponentProps) {
	const { t } = useTranslation('common')
	const [loading, setLoading] = useState(false)

	const id = useId()
	const [searchParams] = useSearchParams()
	const submit = useSubmit()

	const {
		navigation: { route },
	} = usePublicContext()

	useEffect(() => {
		route.setContent([
			{
				label: `${t('search.actions.search')}.`,
			},
		])
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleFormChange = useDebounce(async (form: HTMLFormElement) => {
		await submit(form)
		setLoading(false)
	}, 400)

	return (
		<div className="mx-auto flex min-h-[calc(100vh-var(--header-height))] max-w-[90vw] flex-col items-center justify-center gap-xl lg:max-w-screen-lg">
			<div
				className={cn(
					'flex w-full flex-col justify-center gap-sm self-center',
					loaderData.search ? 'mt-[10vh]' : '',
				)}
			>
				<Form
					method="GET"
					className={'relative'}
					onChange={(e) => {
						if (!loading) setLoading(true)
						handleFormChange(e.currentTarget)
					}}
					onSubmit={(e) => {
						if (!loading) setLoading(true)
						handleFormChange(e.currentTarget)
					}}
				>
					<PublicInput
						type="text"
						name="search"
						id={id}
						defaultValue={searchParams.get('search') ?? ''}
						placeholder={t('search.placeholder')}
						className="w-full placeholder:!normal-case"
						autoFocus
					/>

					<div className={'absolute right-0 top-px text-muted-foreground'}>
						{loading ? (
							<Spinner />
						) : (
							<Button
								variant={'ghost'}
								className={
									'px-0 font-medium lowercase tracking-[0.2em] text-muted-foreground'
								}
								type="submit"
							>
								{t('search.actions.search')}
							</Button>
						)}
					</div>
				</Form>

				<h1 className={'sr-only'}>{t('search.actions.search')}</h1>

				{/* Results section, only show if there is a search term present */}
				{loaderData.search && (
					<div className="flex w-full flex-col gap-sm">
						{/* Collections data */}
						{loaderData.collections!.length > 0 && (
							<>
								{loaderData.collections!.map((collection) => (
									<Link
										viewTransition
										to={routes.public.product.route({
											slug: collection.slug,
										})}
										key={collection.slug}
										className={'flex flex-col justify-between gap-sm'}
									>
										<div className={'flex items-baseline justify-between'}>
											<Text as={'h2'}>{collection.name}</Text>
											<Text>/ {t('search.category.products')} /</Text>
										</div>
										<MarketingImage
											className={'h-[12vh] w-1/3 self-end'}
											imageClassName={''}
											src={getMaterialDesignImgSrc(
												collection.designsImages[0]!.id,
											)}
										/>
									</Link>
								))}
							</>
						)}

						{/*	Articles data */}
						{loaderData.articles!.length > 0 && (
							<>
								{loaderData.articles!.map((article) => (
									<Link
										viewTransition
										to={routes.public.blog.post.route({
											slug: article.translations[0]!.slug,
										})}
										key={article.translations[0]!.slug}
										className={'flex flex-col justify-between gap-sm'}
									>
										<div
											className={'flex items-baseline justify-between gap-sm'}
										>
											<Text as={'h2'}>
												{article.translations[0]!.content ? (
													<span
														className={'line-clamp-1'}
														dangerouslySetInnerHTML={{
															__html: article.translations[0]!.content,
														}}
													></span>
												) : (
													article.translations[0]!.title
												)}
											</Text>
											<Text className={'min-w-fit'}>
												/{' '}
												<Trans
													i18nKey={'search.category.articles'}
													values={{
														category:
															article.categories[0]!.translations[0]!.name,
													}}
												></Trans>{' '}
												/
											</Text>
										</div>
									</Link>
								))}
							</>
						)}

						{/* No results found */}
						{loaderData.articles!.length === 0 &&
							loaderData.collections!.length === 0 && (
								<div className={'py-sm text-center'}>
									<Text as={'p'}>{t('search.noResults')}</Text>
								</div>
							)}
					</div>
				)}
			</div>
		</div>
	)
}
